/**
 * Google Tag Manager — envio de eventos para o `dataLayer`.
 *
 * O container é injetado por `public/index.html`, que carrega `tag-manager.js`
 * da API — script montado por franquia a partir do domínio. Ele roda antes do
 * bundle React, então `window.dataLayer` já existe quando estes helpers são
 * chamados. Sem container o array simplesmente acumula os eventos e nada é
 * enviado, então não há necessidade de checar o ID aqui.
 *
 * Os nomes de evento e de parâmetro abaixo são o contrato com o GTM: alterar
 * qualquer um deles quebra os gatilhos configurados no painel.
 */

type DataLayerEvent = Record<string, unknown> & { event: string };

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

/**
 * Empurra um evento para o `dataLayer`.
 */
export function pushGtmEvent(event: DataLayerEvent): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(event);
}

/** Visualização de página — carga inicial e cada troca de rota da SPA. */
export function pushPageView(url: string): void {
  pushGtmEvent({ event: "page_view", url });
}

/** Primeira interação do usuário com um campo do formulário de lead. */
export function pushFormStart(): void {
  pushGtmEvent({ event: "form_start" });
}

/** Página de cadastro concluído exibida. */
export function pushThankYouPageView(): void {
  pushGtmEvent({ event: "thank_you_page_view" });
}

/** Clique no botão de WhatsApp da página de cadastro concluído. */
export function pushWppThankYouClick(): void {
  pushGtmEvent({ event: "wpp_thankyou_click" });
}

/** Primeiro scroll do usuário na rota atual — dispara uma única vez. */
export function pushPageScroll(): void {
  pushGtmEvent({ event: "page_scroll" });
}

/** Marco de profundidade de scroll atingido, em porcentagem. */
export function pushPercentScrolled(percent: number): void {
  pushGtmEvent({ event: "percent_scrolled", percent_scrolled: percent });
}

/** Marco de tempo de permanência na página, em segundos visíveis. */
export function pushTimerEngagement(seconds: number): void {
  pushGtmEvent({ event: "timer_engagement", seconds });
}

/** Abertura de uma pergunta do FAQ. */
export function pushClickFaq(perguntaTitulo: string): void {
  pushGtmEvent({ event: "click_faq", pergunta_titulo: perguntaTitulo });
}

/** Clique no CTA de uma seção. `sessionName` é o nome da seção no CMS. */
export function pushClickSessionCta(sessionName: string): void {
  pushGtmEvent({ event: "click_session_cta", session_name: sessionName });
}

/** Clique no botão flutuante de WhatsApp. */
export function pushWppFloatingClick(): void {
  pushGtmEvent({ event: "wpp_floating_click" });
}
